import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { ArrowLeft } from 'lucide-react';
import { logger, devLog } from '@/utils/logger';
import { awsAPI } from '@/services/awsAPI';
import { stripeAPI } from '@/services/stripeAPI';
import { customerAPI } from '@/services/customerAPI';
import { accountServiceAPI } from '@/services/accountServiceAPI';
import { cognitoAuth } from '@/services/cognitoAuth';
import { calculateEstimatedCost, generateJobId } from '@/utils';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { SavedPaymentMethodConfirmation } from '@/components/payment/SavedPaymentMethodConfirmation';
import { BatchContactInfoForm, BatchPaymentAuthForm, BatchOrderSummary } from '@/components/batch/payment';
import type {
  PaymentData,
  StripePaymentData,
  ProcessingJob,
  NavigationState,
  AccountInviteStatus,
} from '@/types';
import { resolveProcessingRate, PAYMENT_STORAGE_KEY } from '@/types';
import { useQuery } from '@tanstack/react-query';

if (!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) {
  throw new Error('VITE_STRIPE_PUBLISHABLE_KEY environment variable is required');
}

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
const elementsAppearance = {
  theme: 'stripe' as const,
};

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state as NavigationState | undefined;
  const { isAuthenticated, user, signIn, refreshUser } = useCustomerAuth();

  // Fetch saved payment methods for authenticated users
  const { data: savedPaymentMethods, isLoading: isLoadingPaymentMethods } = useQuery({
    queryKey: ['payment-methods', user?.userId],
    queryFn: () => customerAPI.getPaymentMethods(user!.userId),
    enabled: isAuthenticated && !!user?.userId,
  });

  // Get the default payment method
  const defaultPaymentMethod = savedPaymentMethods?.find(pm => pm.isDefault) || savedPaymentMethods?.[0];

  const job = useMemo<ProcessingJob | null>(() => {
    if (!navigationState) {
      return null;
    }

    const processableRows = navigationState.processableRows ?? navigationState.rowCount ?? 0;
    const totalRows = navigationState.totalRows ?? navigationState.rowCount ?? processableRows;
    const processingType = navigationState.processingType ?? 'validate';
    const jobId = navigationState.jobId ?? generateJobId();
    const aiOverview = navigationState.aiOverview ?? false;
    const estimatedCost = navigationState.estimatedCost ?? calculateEstimatedCost(processableRows, processingType, {
      aiOverview,
    });
    const maxPossibleCost = navigationState.maxPossibleCost ?? calculateEstimatedCost(totalRows, processingType, {
      aiOverview,
    });

    return {
      jobId,
      file: navigationState.file,
      fileName: navigationState.file?.name ?? navigationState.fileName,
      processingType,
      processableRows,
      totalRows,
      estimatedCost,
      maxPossibleCost,
      s3Key: navigationState.s3Key as string,
      s3Bucket: navigationState.s3Bucket as string,
      userId: navigationState.userId,
      companyName: navigationState.companyName,
      aiOverview,
      savePaymentMethod: navigationState.savePaymentMethod ?? true,
    };
  }, [navigationState]);

  useEffect(() => {
    if (!job) {
      navigate('/batch', { replace: true });
    }
  }, [job, navigate]);

  const [paymentData, setPaymentData] = useState<PaymentData>(() => {
    // Pre-fill from authenticated user if available
    if (isAuthenticated && user) {
      const nameParts = user.name?.split(' ') || [];
      return {
        email: user.email,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        companyName: user.companyName || navigationState?.companyName || '',
        agreeToTerms: false,
        fcraAcknowledgment: false,
      };
    }
    // Otherwise use navigation state
    return {
      email: navigationState?.userId ?? '',
      firstName: '',
      lastName: '',
      companyName: navigationState?.companyName ?? '',
      agreeToTerms: false,
      fcraAcknowledgment: false,
    };
  });

  const [stripeData, setStripeData] = useState<StripePaymentData | null>(null);
  const [isInitializingIntent, setIsInitializingIntent] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [createAccount] = useState(true);
  const [savePaymentMethod, setSavePaymentMethod] = useState(() => navigationState?.savePaymentMethod ?? true);
  const [accountInviteStatus, setAccountInviteStatus] = useState<AccountInviteStatus | null>(null);
  const [useSavedPaymentMethod, setUseSavedPaymentMethod] = useState(true); // Start with saved payment method if available
  const [accountPassword, setAccountPassword] = useState('');
  const [accountPasswordConfirm, setAccountPasswordConfirm] = useState('');
  const [accountPasswordError, setAccountPasswordError] = useState<string | null>(null);
  const [accountMode, setAccountMode] = useState<'create' | 'login'>('create');
  const [accountSetupComplete, setAccountSetupComplete] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [accountFeedback, setAccountFeedback] = useState<string | null>(null);
  const [accountSetupMethod, setAccountSetupMethod] = useState<'none' | 'new' | 'existing'>('none');

  useEffect(() => {
    if (isAuthenticated) {
      setAccountSetupComplete(true);
      setAccountFeedback(null);
      setAccountPasswordError(null);
      setLoginError(null);
      setAccountSetupMethod((prev) => (prev === 'none' ? 'existing' : prev));

      // Pre-fill payment data from user account
      if (user && !paymentData.firstName && !paymentData.lastName) {
        const nameParts = user.name?.split(' ') || [];
        setPaymentData(prev => ({
          ...prev,
          email: user.email,
          firstName: nameParts[0] || prev.firstName,
          lastName: nameParts.slice(1).join(' ') || prev.lastName,
          companyName: user.companyName || prev.companyName,
        }));
      }
    }
  }, [isAuthenticated, user, paymentData.firstName, paymentData.lastName]);

  useEffect(() => {
    if (!isAuthenticated && accountMode === 'login') {
      setSavePaymentMethod(false);
    }
  }, [accountMode, isAuthenticated]);

  const handleInputChange = (field: keyof PaymentData, value: string | boolean) => {
    setPaymentData((prev) => ({ ...prev, [field]: value }));
    if (field === 'email') {
      setAccountInviteStatus(null);
      setAccountPasswordError(null);
      setAccountMode('create');
      setAccountSetupComplete(false);
      setLoginPassword('');
      setLoginError(null);
      setAccountFeedback(null);
      setAccountSetupMethod('none');
      setAccountPassword('');
      setAccountPasswordConfirm('');
    }
    setError('');
  };

  const handleAccountModeChange = (mode: 'create' | 'login') => {
    setAccountMode(mode);
    setAccountPasswordError(null);
    setLoginError(null);
    setAccountFeedback(null);
  };

  const ensureAccountSetup = async (): Promise<boolean> => {
    if (isAuthenticated || !createAccount) {
      setAccountSetupComplete(true);
      if (isAuthenticated) {
        setAccountSetupMethod((prev) => (prev === 'none' ? 'existing' : prev));
      }
      return true;
    }

    if (accountSetupComplete) {
      return true;
    }

    const email = paymentData.email.trim();
    const firstName = paymentData.firstName.trim();
    const lastName = paymentData.lastName.trim();

    if (accountMode === 'create') {
      const password = accountPassword.trim();
      let shouldClearPasswords = false;

      try {
        const inviteResponse = await accountServiceAPI.createAccountInvite({
          email,
          firstName,
          lastName,
          companyName: paymentData.companyName,
          jobId: job?.jobId,
          processingType: job?.processingType,
          password,
        });

        if (inviteResponse.userExists) {
          setAccountMode('login');
          setAccountSetupComplete(false);
          setAccountSetupMethod('existing');
          setAccountFeedback('An account with this email already exists. Please sign in to continue.');
          setLoginError('An account with this email already exists. Sign in to continue.');
          return false;
        }

        if (inviteResponse.success && inviteResponse.passwordSet) {
          try {
            await signIn(email, password);
            refreshUser();
            setAccountFeedback('Account created and you are now signed in. Continue with payment to submit your batch.');
            setAccountSetupMethod('new');
            shouldClearPasswords = true;
          } catch (autoLoginError) {
            const autoMessage =
              autoLoginError instanceof Error
                ? autoLoginError.message
                : 'Automatic sign-in failed. Please sign in manually from the login page.';
            setAccountFeedback(`Account created successfully, but automatic sign-in failed: ${autoMessage}`);
            setAccountSetupMethod('new');
          }
        } else if (inviteResponse.success) {
          setAccountFeedback(
            inviteResponse.message ?? 'Account created. Check your email to confirm and then sign in to view results.'
          );
          setAccountSetupMethod('new');
        }

        setAccountSetupComplete(true);
        setAccountPasswordError(null);

        if (shouldClearPasswords) {
          setAccountPassword('');
          setAccountPasswordConfirm('');
        }

        return true;
      } catch (inviteError) {
        const errorCode =
          typeof inviteError === 'object' && inviteError !== null && 'errorCode' in inviteError
            ? (inviteError as { errorCode?: string }).errorCode
            : undefined;
        const fallbackMessage =
          inviteError instanceof Error
            ? inviteError.message
            : 'We were unable to create your account. Please try again.';

        if (errorCode === 'PASSWORD_NOT_SET' || errorCode === 'PASSWORD_NOT_CONFIRMED') {
          setAccountFeedback(
            'We created your account but need you to finish the password setup from the invite email. Once done, sign in to continue.'
          );
          setAccountSetupComplete(true);
          setAccountSetupMethod('new');
          return true;
        }

        setAccountPasswordError(fallbackMessage);
        return false;
      }
    }

    const password = loginPassword.trim();

    if (!password) {
      setLoginError('Enter your password to sign in before checkout.');
      return false;
    }

    try {
      await signIn(email, password);
      refreshUser();
      setAccountSetupComplete(true);
      setLoginPassword('');
      setLoginError(null);
      setAccountFeedback('Signed in successfully. Continue with payment to submit your batch.');
      setAccountSetupMethod('existing');
      return true;
    } catch (loginErr) {
      const loginMessage = loginErr instanceof Error ? loginErr.message : 'Sign in failed. Please try again.';
      const normalizedMessage = loginMessage.includes('Password change required')
        ? 'Your account needs a new password before signing in. Complete the reset from the login page, then return to finish checkout.'
        : loginMessage;
      setLoginError(normalizedMessage);
      return false;
    }
  };

  const validateContactInformation = () => {
    if (!paymentData.email || !paymentData.email.includes('@')) {
      setError('Please enter a valid email address.');
      return false;
    }

    if (!paymentData.firstName.trim()) {
      setError('Please provide your first name.');
      return false;
    }

    if (!paymentData.lastName.trim()) {
      setError('Please provide your last name.');
      return false;
    }

    if (!paymentData.agreeToTerms || !paymentData.fcraAcknowledgment) {
      setError('Please agree to the terms and conditions before continuing.');
      return false;
    }

    if (!isAuthenticated && createAccount) {
      if (accountMode === 'create') {
        const password = accountPassword.trim();
        const confirmPassword = accountPasswordConfirm.trim();

        if (password.length < 8) {
          setAccountPasswordError('Password must be at least 8 characters long.');
          return false;
        }

        if (password !== confirmPassword) {
          setAccountPasswordError('Passwords do not match.');
          return false;
        }

        setAccountPasswordError(null);
        setLoginError(null);
      } else {
        if (!loginPassword.trim()) {
          setLoginError('Enter your password to sign in before checkout.');
          return false;
        }
        setLoginError(null);
        setAccountPasswordError(null);
      }
    } else {
      setAccountPasswordError(null);
      setLoginError(null);
    }

    return true;
  };

  const initializePaymentIntent = async () => {
    if (!job) {
      return;
    }

    if (!validateContactInformation()) {
      return;
    }

    const accountReady = await ensureAccountSetup();
    if (!accountReady) {
      return;
    }

    setIsInitializingIntent(true);
    setError('');

    try {
      const response = await stripeAPI.createPaymentIntent({
        maxAmount: job.maxPossibleCost,
        email: paymentData.email,
        firstName: paymentData.firstName,
        lastName: paymentData.lastName,
        processingType: job.processingType,
        s3Key: job.s3Key,
        s3Bucket: job.s3Bucket,
        companyName: paymentData.companyName,
        fileName: job.file?.name ?? job.fileName,
        totalRows: job.totalRows,
        processableRows: job.processableRows,
        jobId: job.jobId,
        estimatedCost: job.estimatedCost,
        aiOverview: job.aiOverview ?? false,
        savePaymentMethod,
      });

      setStripeData({
        paymentIntentId: response.payment_intent_id,
        clientSecret: response.client_secret,
      });
      logger.info('Payment intent initialized successfully', {
        paymentIntentId: response.payment_intent_id,
        jobId: job.jobId,
      });
    } catch (intentError) {
      logger.error('Failed to initialize payment intent', intentError);
      setError(intentError instanceof Error ? intentError.message : 'Unable to create payment intent.');
      setStripeData(null);
    } finally {
      setIsInitializingIntent(false);
    }
  };

  const sendAccountInvite = async (targetJob?: ProcessingJob): Promise<AccountInviteStatus | null> => {
    const jobForInvite = targetJob ?? job;

    if (!createAccount || !jobForInvite) {
      return null;
    }

    if (accountSetupMethod === 'existing' && isAuthenticated) {
      const status: AccountInviteStatus = {
        invited: true,
        requiresConfirmation: false,
        message: 'Signed in with your existing account. This batch will appear in your dashboard once processing completes.',
      };
      setAccountInviteStatus(status);
      return status;
    }

    try {
      const password = accountPassword.trim();
      const response = await awsAPI.requestAccountInvite({
        email: paymentData.email.trim(),
        firstName: paymentData.firstName,
        lastName: paymentData.lastName,
        companyName: paymentData.companyName,
        jobId: jobForInvite.jobId,
        processingType: jobForInvite.processingType,
        password:
          !isAuthenticated && accountSetupMethod !== 'existing' ? accountPassword.trim() || undefined : undefined,
      });

      const status: AccountInviteStatus = {
        invited: response.inviteSent ?? response.success,
        requiresConfirmation:
          response.requiresConfirmation ?? (!response.passwordSet && accountSetupMethod !== 'existing'),
        message: response.userExists
          ? accountSetupMethod === 'new'
            ? 'Account created successfully and already linked. This batch will be available in your dashboard shortly.'
            : 'Account located. This batch will be available in your dashboard shortly.'
          : response.message ?? 'Account invitation processed successfully.',
      };

      if (response.success && response.passwordSet && !isAuthenticated) {
        try {
          await signIn(paymentData.email.trim(), password);
          refreshUser();

          // NOTE: Guest batch linking removed - batch history is saved with cognitoUserId after processing completes
          status.message = 'Account created and you are now signed in. This batch will appear in your dashboard once processing completes.';

          status.requiresConfirmation = false;
          setAccountPassword('');
          setAccountPasswordConfirm('');
          setAccountPasswordError(null);
        } catch (autoLoginError) {
          logger.warn('Automatic sign-in failed after account creation', autoLoginError);
          status.error =
            'Account created, but automatic sign-in failed. Please sign in manually after processing completes.';
        }
      }

      setAccountInviteStatus(status);
      return status;
    } catch (inviteError) {
      logger.error('Failed to request account invite', inviteError);

      const errorCode =
        typeof inviteError === 'object' && inviteError !== null && 'errorCode' in inviteError
          ? (inviteError as { errorCode?: string }).errorCode
          : undefined;

      const fallbackMessage =
        inviteError instanceof Error
          ? inviteError.message
          : 'We were unable to create an account invite. You can request access later from support.';

      const contextualMessage =
        errorCode === 'PASSWORD_NOT_SET' || errorCode === 'PASSWORD_NOT_CONFIRMED'
          ? 'We created the account, but could not finalize the password automatically. Check your email for the invite to finish setup.'
          : fallbackMessage;

      const status: AccountInviteStatus = {
        invited: false,
        error: contextualMessage,
      };
      setAccountInviteStatus(status);
      if (accountMode === 'login') {
        setLoginError(contextualMessage);
      } else {
        setAccountPasswordError(contextualMessage);
      }
      return status;
    }
  };

  const extractBackendJobId = (response: unknown, fallbackId: string): string => {
    // Type guard for response object
    if (!response || typeof response !== 'object') {
      devLog.warn('Response is not an object, using fallback:', fallbackId);
      return fallbackId;
    }

    const responseObj = response as Record<string, unknown>;

    // Check direct fields first
    if (responseObj.jobId && typeof responseObj.jobId === 'string') {
      devLog.info('Backend job ID found in response.jobId:', responseObj.jobId);
      return responseObj.jobId;
    }

    if (responseObj.job_id && typeof responseObj.job_id === 'string') {
      devLog.info('Backend job ID found in response.job_id:', responseObj.job_id);
      return responseObj.job_id;
    }

    if (responseObj.job && typeof responseObj.job === 'string') {
      devLog.info('Backend job ID found in response.job:', responseObj.job);
      return responseObj.job;
    }

    if (responseObj.id && typeof responseObj.id === 'string') {
      devLog.info('Backend job ID found in response.id:', responseObj.id);
      return responseObj.id;
    }

    // Try to extract UUID from message field
    if (responseObj.message && typeof responseObj.message === 'string') {
      const uuidRegex = /([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i;
      const match = responseObj.message.match(uuidRegex);
      if (match) {
        devLog.info('Backend job ID extracted from message:', match[1]);
        return match[1];
      }
    }

    // Fallback to original ID
    devLog.warn('No backend job ID found, using fallback:', fallbackId);
    return fallbackId;
  };


  const handleConfirmSavedPaymentMethod = async () => {
    if (!job || !defaultPaymentMethod || !user) {
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      // Create payment intent with saved payment method attached
      const response = await stripeAPI.createPaymentIntent({
        maxAmount: job.maxPossibleCost,
        email: user.email,
        firstName: paymentData.firstName || user.name || '',
        lastName: paymentData.lastName || '',
        processingType: job.processingType,
        s3Key: job.s3Key,
        s3Bucket: job.s3Bucket,
        companyName: user.companyName,
        fileName: job.file?.name ?? job.fileName,
        totalRows: job.totalRows,
        processableRows: job.processableRows,
        jobId: job.jobId,
        estimatedCost: job.estimatedCost,
        aiOverview: job.aiOverview ?? false,
        savePaymentMethod: true, // Already have a saved method
        paymentMethodId: defaultPaymentMethod.stripePaymentMethodId, // NEW: Pass saved payment method ID
      });

      const newStripeData = {
        paymentIntentId: response.payment_intent_id,
        clientSecret: response.client_secret,
      };

      setStripeData(newStripeData);

      // Immediately proceed with processing since we have authorization
      // Pass stripe data directly to avoid state timing issues
      await handlePaymentSuccessWithData(newStripeData);
    } catch (error) {
      logger.error('Failed to authorize with saved payment method', error);
      setError(error instanceof Error ? error.message : 'Unable to authorize payment with saved method.');
      setIsProcessing(false);
    }
  };

  const handlePaymentSuccess = async () => {
    if (!stripeData) {
      logger.error('handlePaymentSuccess called without stripeData');
      return;
    }
    return handlePaymentSuccessWithData(stripeData);
  };

  const handlePaymentSuccessWithData = async (stripeDataParam: StripePaymentData) => {
    if (!job) {
      return;
    }

    setIsProcessing(true);
    setError('');

    // Log the complete job state including AI Overview
    devLog.info('📊 Payment Success - Complete Job State:', {
      jobId: job.jobId,
      processingType: job.processingType,
      aiOverview: job.aiOverview,
      totalRows: job.totalRows,
      processableRows: job.processableRows,
      estimatedCost: job.estimatedCost,
      s3Bucket: job.s3Bucket,
      s3Key: job.s3Key,
      userId: job.userId,
      companyName: job.companyName
    });

    let accountStatus: AccountInviteStatus | null = null;

    try {
      // Get current user to check if we have a cognitoUserId
      const currentUser = cognitoAuth.getCurrentUser();

      const processingPayload = {
        s3Bucket: job.s3Bucket,
        s3Key: job.s3Key,
        jobId: job.jobId,
        operation: job.processingType,
        companyId: job.companyName ?? 'CollectABILITY-Demo',
        userId: job.userId ?? paymentData.email,
        aiOverview: job.aiOverview ?? false,
        guest_email: paymentData.email, // For guest batch storage (fallback)
        cognitoUserId: currentUser?.userId, // Pass cognitoUserId if user is already authenticated
        filename: job.fileName || job.file?.name || 'unknown.csv', // For batch history display
      };

      devLog.info('🚀 Starting Backend Processing with Payload:', processingPayload);

      const processingResponse = await awsAPI.startProcessing(processingPayload);

      // Log the full response
      devLog.info('🎯 Backend Processing Response:', processingResponse);

      // Extract backend job ID using the helper
      const backendJobId = extractBackendJobId(processingResponse, job.jobId);

      // Log the ID mapping
      devLog.info('Job ID mapping:', {
        clientJobId: job.jobId,
        backendJobId: backendJobId,
        isNewId: backendJobId !== job.jobId,
      });

      const nextJob: ProcessingJob = {
        ...job,
        jobId: backendJobId,
        totalRows: processingResponse.totalRecords ?? job.totalRows,
      };

      accountStatus = await sendAccountInvite(nextJob);

      if (backendJobId !== job.jobId) {
        try {
          sessionStorage.removeItem(PAYMENT_STORAGE_KEY(job.jobId));
        } catch (storageError) {
          logger.warn('Failed to remove stale payment metadata', storageError);
        }
      }

      const navigationPayload = {
        job: { ...nextJob, savePaymentMethod },
        paymentIntentId: stripeDataParam.paymentIntentId,
        clientSecret: stripeDataParam.clientSecret,
        paymentData,
        accountInviteStatus: accountStatus ?? accountInviteStatus ?? undefined,
        savePaymentMethod,
      };

      try {
        sessionStorage.setItem(
          PAYMENT_STORAGE_KEY(backendJobId),
          JSON.stringify(navigationPayload),
        );
      } catch (storageError) {
        logger.warn('Failed to persist payment metadata in session storage', storageError);
      }

      navigate(`/batch/processing/${backendJobId}`, {
        replace: true,
        state: navigationPayload,
      });
    } catch (processError) {
      logger.error('Failed to trigger backend processing', processError);
      const message =
        processError instanceof Error
          ? processError.message
          : 'Failed to start processing job. Payment has been authorized but no processing was initiated.';
      setError(message);

      try {
        await stripeAPI.cancelPayment({
          paymentIntentId: stripeDataParam.paymentIntentId,
          reason: message,
        });
      } catch (cancelError) {
        logger.error('Failed to cancel payment intent after processing failure', cancelError);
      }

      setStripeData(null);
      setIsProcessing(false);
    }
  };

  if (!job) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Preparing payment details...</p>
        </div>
      </div>
    );
  }

  const processingRate = resolveProcessingRate(job.processingType, job.processableRows, job.aiOverview ?? false);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-sm text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </button>
            <div className="text-sm text-slate-500">Job #{job.jobId.split('_').pop()}</div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <section className="lg:col-span-2 space-y-3">
            {/* Show loading state while checking for saved payment methods */}
            {isAuthenticated && isLoadingPaymentMethods ? (
              <div className="ca-card p-4">
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mr-3" />
                  <p className="text-slate-600">Loading payment options...</p>
                </div>
              </div>
            ) : isAuthenticated && defaultPaymentMethod && useSavedPaymentMethod && !stripeData ? (
              /* Show saved payment method confirmation for logged-in users with saved methods */
              <SavedPaymentMethodConfirmation
                paymentMethod={defaultPaymentMethod}
                estimatedCost={job.estimatedCost}
                maxPossibleCost={job.maxPossibleCost}
                onConfirm={handleConfirmSavedPaymentMethod}
                onUseDifferentMethod={() => setUseSavedPaymentMethod(false)}
                isProcessing={isProcessing}
              />
            ) : (
              <>
                <BatchContactInfoForm
                  paymentData={paymentData}
                  onInputChange={handleInputChange}
                  isAuthenticated={isAuthenticated}
                  accountMode={accountMode}
                  onAccountModeChange={handleAccountModeChange}
                  accountPassword={accountPassword}
                  onAccountPasswordChange={setAccountPassword}
                  accountPasswordConfirm={accountPasswordConfirm}
                  onAccountPasswordConfirmChange={setAccountPasswordConfirm}
                  loginPassword={loginPassword}
                  onLoginPasswordChange={setLoginPassword}
                  accountPasswordError={accountPasswordError}
                  loginError={loginError}
                  accountFeedback={accountFeedback}
                  accountInviteStatus={accountInviteStatus}
                  onInitializePayment={initializePaymentIntent}
                  isInitializingIntent={isInitializingIntent}
                  isProcessing={isProcessing}
                  stripeData={stripeData}
                  error={error}
                  job={job}
                />

                {stripeData && (
                  <Elements
                    stripe={stripePromise}
                    options={{ clientSecret: stripeData.clientSecret, appearance: elementsAppearance }}
                  >
                    <BatchPaymentAuthForm
                      onSubmit={handlePaymentSuccess}
                      stripeData={stripeData}
                      isSubmitting={isProcessing}
                      savePaymentMethod={savePaymentMethod}
                      onSavePaymentMethodChange={setSavePaymentMethod}
                    />
                  </Elements>
                )}
              </>
            )}
          </section>

          <aside className="lg:col-span-1 space-y-3">
            <BatchOrderSummary job={job} processingRate={processingRate} />
          </aside>
        </div>
      </main>

      {isProcessing && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg  p-8 max-w-md w-full text-center"
          >
            <div className="w-12 h-10 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Starting secure processing</h2>
            <p className="text-sm text-slate-600">
              We&apos;ve authorized your payment and are handing the job off to the processing queue. This usually takes a few seconds.
            </p>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PaymentPage;
